import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const STATUS_META = {
  present: { label:'Present', color:'bg-green-100 text-green-700', dot:'bg-green-500' },
  late:    { label:'Late',    color:'bg-yellow-100 text-yellow-700', dot:'bg-yellow-500' },
  absent:  { label:'Absent',  color:'bg-red-100 text-brand-600',   dot:'bg-brand-500'  },
  excused: { label:'Excused', color:'bg-blue-100 text-blue-700',   dot:'bg-blue-500'   },
};

export default function StudentAttendancePage() {
  const { addToast } = useToast();
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    API.get('/attendance/my-attendance')
      .then(({ data }) => { setAttendance(data.attendance || []); setSummary(data.summary || null); })
      .catch(() => addToast('Failed to load attendance', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Group by month
  const byMonth = attendance.reduce((acc, r) => {
    const key = r.date?.slice(0,7) || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const pct     = summary?.percentage ?? 100;
  const atRisk  = pct < 80;

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" className="text-brand-500"/></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader title="My Attendance" subtitle="Track your class attendance record"/>

      {/* Summary card */}
      {summary && (
        <div className={`mb-6 p-5 rounded-2xl border-2 flex items-center gap-6 flex-wrap
          ${atRisk ? 'bg-red-50 border-brand-300' : 'bg-green-50 border-green-300'}`}>
          {/* Circular pct */}
          <div className="flex items-center justify-center">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#e5e7eb" strokeWidth="3"/>
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke={atRisk ? '#DD0000' : '#16a34a'} strokeWidth="3"
                  strokeDasharray={`${pct}, 100`}/>
              </svg>
              <span className={`absolute inset-0 flex items-center justify-center text-base font-bold ${atRisk ? 'text-brand-500' : 'text-green-700'}`}>
                {pct}%
              </span>
            </div>
          </div>
          <div className="flex-1">
            <p className={`text-base font-bold ${atRisk ? 'text-brand-500' : 'text-green-700'}`}>
              {atRisk ? '⚠️ Attendance Below 80%' : '✅ Good Attendance'}
            </p>
            {atRisk && <p className="text-sm text-brand-500 mt-1">Please attend your scheduled classes to maintain your enrollment.</p>}
            <div className="flex gap-4 mt-3 flex-wrap">
              {[['Present',summary.present,'green'],['Late',summary.late,'yellow'],['Absent',summary.absent,'red'],['Total',summary.total,'gray']].map(([l,v,c])=>(
                <div key={l} className="text-center">
                  <p className={`text-lg font-bold text-${c}-600`}>{v}</p>
                  <p className="text-xs text-gray-500">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {attendance.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-bold text-de-black mb-1">No attendance records yet</p>
          <p className="text-sm text-gray-500">Your attendance will appear here once classes begin.</p>
        </Card>
      ) : (
        Object.keys(byMonth).sort().reverse().map(month => (
          <div key={month} className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 px-1">
              {new Date(month + '-01').toLocaleDateString('en-GB', { month:'long', year:'numeric' })}
            </h3>
            <Card className="overflow-hidden">
              {byMonth[month].map((r, i) => {
                const meta = STATUS_META[r.status] || STATUS_META.absent;
                return (
                  <div key={i} className={`flex items-center justify-between px-5 py-3 ${i>0?'border-t border-gray-100':''} hover:bg-gray-50`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`}/>
                      <div>
                        <p className="text-sm font-medium text-de-black">
                          {new Date(r.date).toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}
                        </p>
                        {r.cohort_name && <p className="text-xs text-gray-400">{r.cohort_name}</p>}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </Card>
          </div>
        ))
      )}
    </div>
  );
}
